import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaExclamationTriangle } from 'react-icons/fa';
import './EventForm.css';
import config from '../config/config';
import { getEventCountForDate } from '../utils/eventLimits';
import { useAuth } from '../hooks/useAuth';

const EventForm = () => {
  const { eventId } = useParams(); // Get eventId from URL parameters
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { user } = useAuth();

  const apiUrl = `${config.API_URL}/api`;

  const [formData, setFormData] = useState({
    name: '',
    date: '',
    startTime: '',
    endTime: '',
    location: '',
    description: '',
    phone1: '',
    phone2: '',
    phone3: '',
    numberMusicians: '',
    price: '',
    prepayment: '',
    singers: [] // Ensure singers is initialized as an array
  });

  const [singers, setSingers] = useState([]); // List of available singers
  const [isFormModified, setIsFormModified] = useState(false);
  const [isSingersListVisible, setIsSingersListVisible] = useState(false); // To control visibility of the singer list
  const [eventCount, setEventCount] = useState(0); // Track event count for selected date
  const [showEventLimitWarning, setShowEventLimitWarning] = useState(false);

  // Fetch singers when the component mounts
  useEffect(() => {
    const fetchSingers = async () => {
      try {
        const response = await axios.get(`${apiUrl}/singers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSingers(response.data || []); // Ensure singers is an array
      } catch (error) {
        console.error("Error fetching singers:", error);
      }
    };

    fetchSingers();

    // If editing an event, fetch event data
    if (eventId) {
      const fetchEventData = async () => {
        try {
          const response = await axios.get(`${apiUrl}/events/${eventId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const eventData = response.data;

          // Format the date to YYYY-MM-DD if necessary
          const formattedDate = eventData.date ? new Date(eventData.date).toISOString().split('T')[0] : '';

          // Extract singer IDs from the artists array
          const singerIds = eventData.artists ? eventData.artists.map(artist => artist.id) : [];

          // Set all event fields, including the singers array
          setFormData({
            ...eventData,
            date: formattedDate, // Set the formatted date
            singers: singerIds, // Set the singer IDs
            numberMusicians: eventData.contract ? eventData.contract.numberMusicians : '', // Fetch from associated contract if available
            price: eventData.contract ? eventData.contract.price : '',
            prepayment: eventData.contract ? eventData.contract.prepayment : ''
          });


        } catch (error) {
          console.error("Error fetching event data:", error);
          alert('Failed to fetch event data.');
        }
      };
      fetchEventData();
    }
  }, [eventId, apiUrl, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setIsFormModified(true);

    // Check event count when date changes (only for non-admin users)
    if (name === 'date' && value && user && user.role !== 'admin') {
      checkEventCountForDate(value);
    }
  };

  // Function to check event count for a specific date
  const checkEventCountForDate = async (date) => {
    try {
      const count = await getEventCountForDate(date, token);
      setEventCount(count);
      setShowEventLimitWarning(count >= 3);
    } catch (error) {
      console.error('Error checking event count:', error);
    }
  };

  const handleSingerSelect = (singerId) => {
    if (!formData.singers.includes(singerId)) {
      setFormData(prevState => ({
        ...prevState,
        singers: [...prevState.singers, singerId] // Add the singer ID to the singers array
      }));


    }
  };

  const handleSingerRemove = (singerId) => {
    setFormData(prevState => ({
      ...prevState,
      singers: prevState.singers.filter(id => id !== singerId), // Remove the singer ID from the singers array
    }));


  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {


      if (eventId) {
        // If editing, send PUT request
        await axios.put(`${apiUrl}/events/${eventId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Event updated successfully!');
      } else {
        // If adding, send POST request
        await axios.post(`${apiUrl}/events`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Event created successfully!');
      }
      navigate('/events');
    } catch (error) {
      console.error('Error submitting form:', error);

      // Check if it's an event limit error
      if (error.response && error.response.data && error.response.data.code === 'EVENT_LIMIT_EXCEEDED') {
        alert(error.response.data.message);
      } else {
        alert('Failed to submit event.');
      }
    }
  };

  const handleBack = () => {
    if (isFormModified) {
      const confirmBack = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (confirmBack) {
        navigate('/events');
      }
    } else {
      navigate('/events');
    }
  };

  const toggleSingersList = () => {
    setIsSingersListVisible(prevState => !prevState);
  };



  return (
    <div className="event-form-page">
      {/* Top Bar with Back Button */}
      <div className="form-page-top-bar">
        <button
          type="button"
          className="back-button"
          onClick={handleBack}
        >
          <FaArrowLeft className="back-icon" />
          Retour
        </button>
      </div>

      <div className="form-container">
        <div className="card shadow-lg">
          <div className="card-header">
            <h1 className="page-title">{eventId ? 'Modifier l\'événement' : 'Ajouter un événement'}</h1>
            <p className="page-subtitle">
              {eventId ? 'Modifiez les détails de l\'événement' : 'Créez un nouvel événement'}
            </p>
          </div>

          <div className="card-body">
            <form className="event-form" onSubmit={handleSubmit}>
              {/* Client details section */}
              <div className="form-section">
                <h2 className="section-title">Informations Client</h2>
                <div className="form-grid">
                  <div className="form-group form-group-full">
                    <label className="form-label">Nom du client:</label>
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Entrez le nom complet"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Event Details Section */}
              <div className="form-section">
                <h2 className="section-title">Détails de l'Événement</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Date de l'événement:</label>
                    <input
                      type="date"
                      name="date"
                      className="form-control"
                      value={formData.date}
                      onChange={handleChange}
                      required
                    />
                    {/* Event count warning for non-admin users */}
                    {user && user.role !== 'admin' && formData.date && (
                      <div className="event-count-info">
                        <p className="event-count-text">
                          Événements le {formData.date}: {eventCount}/3 (tous les utilisateurs)
                        </p>
                        {showEventLimitWarning && (
                          <div className="event-limit-warning">
                            <FaExclamationTriangle className="warning-icon" />
                            <span>Cette journée est complète! Il y a déjà 3 événements ce jour. Seul l'administrateur peut ajouter plus d'événements.</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Lieu:</label>
                    <input
                      type="text"
                      name="location"
                      className="form-control"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Adresse de l'événement"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Heure de début:</label>
                    <input
                      type="time"
                      name="startTime"
                      className="form-control"
                      value={formData.startTime}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Heure de fin:</label>
                    <input
                      type="time"
                      name="endTime"
                      className="form-control"
                      value={formData.endTime}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Nombre de musiciens:</label>
                    <input
                      type="number"
                      name="numberMusicians"
                      className="form-control"
                      value={formData.numberMusicians}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-grid" style={{ marginTop: '24px' }}>
                  <div className="form-group">
                    <label className="form-label">Prix Total (TND):</label>
                    <input
                      type="number"
                      name="price"
                      className="form-control"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Avance Payée (TND):</label>
                    <input
                      type="number"
                      name="prepayment"
                      className="form-control"
                      value={formData.prepayment}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Contact info Section */}
              <div className="form-section">
                <h2 className="section-title">Coordonnées</h2>
                <div className="form-grid phone-trio">
                  <div className="form-group">
                    <label className="form-label">Téléphone 1:</label>
                    <input
                      type="tel"
                      name="phone1"
                      className="form-control"
                      value={formData.phone1}
                      onChange={handleChange}
                      placeholder="Principal..."
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Téléphone 2 (Optionnel):</label>
                    <input
                      type="tel"
                      name="phone2"
                      className="form-control"
                      value={formData.phone2}
                      onChange={handleChange}
                      placeholder="Additionnel..."
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Téléphone 3 (Optionnel):</label>
                    <input
                      type="tel"
                      name="phone3"
                      className="form-control"
                      value={formData.phone3}
                      onChange={handleChange}
                      placeholder="Additionnel..."
                    />
                  </div>
                </div>
              </div>

              {/* Additional Info / Description */}
              <div className="form-section">
                <h2 className="section-title">Informations Complémentaires</h2>
                <div className="form-grid">
                  <div className="form-group form-group-full">
                    <label className="form-label">Description:</label>
                    <textarea
                      name="description"
                      className="form-control"
                      style={{ minHeight: '100px', resize: 'vertical' }}
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Détails supplémentaires..."
                    />
                  </div>
                </div>
              </div>

              {/* Singers Section layout mirroring ContractForm */}
              <div className="form-section">
                <div className="section-header">
                  <h2 className="section-title">Artistes Associés</h2>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={toggleSingersList}>
                    <FaPlus style={{ marginRight: '8px' }} /> Ajouter un chanteur
                  </button>
                </div>

                <div className="form-group">
                  {isSingersListVisible && Array.isArray(singers) && singers.length > 0 && (
                    <div className="singer-list-box mt-3">
                      {singers
                        .filter(singer => !formData.singers.includes(singer.id)) // Filter out selected singers
                        .map(singer => (
                          <div key={singer.id} className="singer-item">
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm w-100 text-left"
                              onClick={() => handleSingerSelect(singer.id)}
                            >
                              {singer.name}
                            </button>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Display selected singers with remove button */}
                  <div className="selected-singers mt-3">
                    {Array.isArray(formData.singers) && formData.singers.map(singerId => {
                      const selectedSinger = singers.find(singer => singer.id === singerId);
                      return selectedSinger ? (
                        <div key={singerId} className="singer-tag">
                          <span>{selectedSinger.name}</span>
                          <button
                            type="button"
                            className="remove-singer"
                            onClick={() => handleSingerRemove(singerId)}
                          >
                            ✕
                          </button>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              </div>

              <div className="form-actions mt-4">
                <div className="action-buttons" style={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={handleBack}>
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {eventId ? 'Mettre à jour l\'événement' : 'Créer un événement'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventForm;
